import * as React from "react";
import {
  Box,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";

export default function InlineEditableSelect({
  value: initialValue,
  options, // [{ value: "a", label: "Option A" }, ...]
  onChange,
  placeholder = "—",
}) {
  const [value, setValue] = React.useState(initialValue);
  const [editing, setEditing] = React.useState(false);
  const selectRef = React.useRef(null);

  // Keep internal state synced with parent
  React.useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  React.useEffect(() => {
    if (editing) {
      // Focus the select input when entering edit mode
      // (Select's internal input is focusable)
      setTimeout(() => selectRef.current?.focus(), 0);
    }
  }, [editing]);

  const currentLabel = options?.find((o) => o.value === value)?.label ?? "";

  const commit = (nextValue) => {
    setValue(nextValue);
    setEditing(false);
    onChange?.(nextValue);
  };

  const cancel = () => {
    setValue(initialValue);
    setEditing(false);
  };

  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      {!editing ? (
        <>
          <Typography variant="body1">{currentLabel || placeholder}</Typography>

          <IconButton
            size="small"
            onClick={() => setEditing(true)}
            aria-label="edit"
            sx={{backgroundColor:"#e0e0e0"}}
          >
            <MoreHorizIcon fontSize="small" />
          </IconButton>
        </>
      ) : (
        <Box
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              e.preventDefault();
              cancel();
            }
          }}
        >
          <Select
            variant="standard"
            size="small"
            value={value ?? ""}
            inputRef={selectRef}
            autoFocus
            open // keep it open immediately when editing (optional; remove if you don't want this)
            onClose={() => setEditing(false)} // closes if user clicks away
            onBlur={() => setEditing(false)} // safety
            onChange={(e) => commit(e.target.value)}
            displayEmpty
            sx={{
              minWidth: 140,
              "& .MuiSelect-select": {
                fontSize: "1rem",
                padding: 0,
              },
            }}
            MenuProps={{
              // helps keep it positioned nicely in inline contexts
              disablePortal: true,
            }}
          >
            {/* Optional "empty" choice */}
            <MenuItem value="">
              <em>{placeholder}</em>
            </MenuItem>

            {options.map((opt) => (
              <MenuItem key={String(opt.value)} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </Box>
      )}
    </Stack>
  );
}
